import React from 'react';
import { Box, Paper, Typography, useTheme } from '@mui/material';

interface FeatureCardProps {
  icon: string;
  title: string;
  description: string;
}

/**
 * Feature card component with icon, title, and description
 * Includes hover effect with lift and shadow enhancement
 */
export const FeatureCard: React.FC<FeatureCardProps> = ({ icon, title, description }) => {
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2.5, md: 4 },
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        borderRadius: { xs: 2, md: 3 },
        backgroundColor: (theme) =>
          theme.palette.mode === 'light' ? '#FFFFFF' : 'rgba(255, 255, 255, 0.05)',
        boxShadow: { xs: '0 2px 12px rgba(0, 0, 0, 0.12)', md: '0 2px 12px rgba(0, 0, 0, 0.08)' },
        transition: theme.transitions.create(['transform', 'box-shadow'], {
          duration: theme.transitions.duration.short,
        }),
        // Hover effect only on devices that support hover (not touch)
        '@media (hover: hover)': {
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 12px 32px rgba(0, 0, 0, 0.12)',
          },
        },
        // Active state for touch devices
        '&:active': {
          transform: 'scale(0.98)',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
        },
      }}
    >
      {/* Icon */}
      <Box
        sx={{
          fontSize: '2.5rem',
          mb: 2,
          lineHeight: 1,
        }}
        role="img"
        aria-label={title}
      >
        {icon}
      </Box>

      {/* Title */}
      <Typography
        variant="h6"
        component="h3"
        sx={{
          fontWeight: 600,
          mb: 1.5,
        }}
      >
        {title}
      </Typography>

      {/* Description */}
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
        {description}
      </Typography>
    </Paper>
  );
};
